export type ManagementReviewInsightTone = "positive" | "risk" | "neutral";

export type ManagementReviewInsightBlock = {
  insightId: string;
  label: string;
  title: string;
  body: string;
  evidence: string;
  suggestedAction?: string;
  tone: ManagementReviewInsightTone;
  sourceHelper: string;
  sourceFields?: string[];
  requiredAccess?: string;
  caveat?: string;
};

type BuildManagementReviewInsightBlocksInput = {
  coverageRatioLabel: string;
  isEarlyStageWeak: boolean;
  isMidStageWeak: boolean;
  isLateStageWeak: boolean;
  urgencyRiskCount: number;
};

function formatWeakStageList(input: BuildManagementReviewInsightBlocksInput) {
  return [
    input.isEarlyStageWeak ? "early-stage" : null,
    input.isMidStageWeak ? "mid-stage" : null,
    input.isLateStageWeak ? "late-stage" : null,
  ].filter(Boolean).join(", ");
}

export function buildManagementReviewInsightBlocks(
  input: BuildManagementReviewInsightBlocksInput,
): ManagementReviewInsightBlock[] {
  const weakStageList = formatWeakStageList(input);
  const weakStageCount = [
    input.isEarlyStageWeak,
    input.isMidStageWeak,
    input.isLateStageWeak,
  ].filter(Boolean).length;

  const coverageBlock: ManagementReviewInsightBlock =
    weakStageCount === 0
      ? {
        insightId: "management_review.coverage_pattern",
        label: "Coverage pattern",
        title: "Pipeline shape is not the issue",
        body: "Coverage is balanced. The management conversation should move to conversion quality and forecast risk.",
        evidence: `${input.coverageRatioLabel} coverage; no weak coverage stages detected by the existing pipeline coverage helper.`,
        suggestedAction: "Do not launch a broad pipeline push. Inspect close-plan quality and risk movement.",
        tone: "positive",
        sourceHelper: "evaluateRiskPipelineCoverage",
        sourceFields: ["coverage", "isEarlyStageWeak", "isMidStageWeak", "isLateStageWeak"],
        requiredAccess: "revenue_intelligence.management_review",
      }
      : weakStageCount > 1
        ? {
          insightId: "management_review.coverage_pattern",
          label: "Coverage pattern",
          title: "Pipeline gap is structural",
          body: "Several funnel stages are weak. This is not a single team or deal problem.",
          evidence: `${input.coverageRatioLabel} coverage; weak stages: ${weakStageList}.`,
          suggestedAction: "Put CRO and CMO in the same room. Fix inflow and stage progression together.",
          tone: "risk",
          sourceHelper: "evaluateRiskPipelineCoverage",
          sourceFields: ["coverage", "isEarlyStageWeak", "isMidStageWeak", "isLateStageWeak"],
          requiredAccess: "revenue_intelligence.management_review",
        }
        : input.isEarlyStageWeak
          ? {
            insightId: "management_review.coverage_pattern",
            label: "Coverage pattern",
            title: "Future revenue is underfed",
            body: "Early-stage coverage is weak. The risk is future-quarter pipeline creation.",
            evidence: `${input.coverageRatioLabel} coverage; weak stage: early-stage.`,
            suggestedAction: "Reallocate attention to qualified pipeline creation by source and segment.",
            tone: "risk",
            sourceHelper: "evaluateRiskPipelineCoverage",
            sourceFields: ["coverage", "isEarlyStageWeak"],
            requiredAccess: "revenue_intelligence.management_review",
          }
          : input.isLateStageWeak
            ? {
              insightId: "management_review.coverage_pattern",
              label: "Coverage pattern",
              title: "Near-term revenue is exposed",
              body: "Late-stage coverage is weak. The quarter depends on conversion, not more pipeline.",
              evidence: `${input.coverageRatioLabel} coverage; weak stage: late-stage.`,
              suggestedAction: "Move executive time to late-stage close plans and unblock decisions.",
              tone: "risk",
              sourceHelper: "evaluateRiskPipelineCoverage",
              sourceFields: ["coverage", "isLateStageWeak"],
              requiredAccess: "revenue_intelligence.management_review",
            }
            : {
              insightId: "management_review.coverage_pattern",
              label: "Coverage pattern",
              title: "Pipeline is getting stuck",
              body: "Mid-stage coverage is weak. Qualified pipeline is not progressing fast enough.",
              evidence: `${input.coverageRatioLabel} coverage; weak stage: mid-stage.`,
              suggestedAction: "Force a stage-progression review before the next forecast call.",
              tone: "risk",
              sourceHelper: "evaluateRiskPipelineCoverage",
              sourceFields: ["coverage", "isMidStageWeak"],
              requiredAccess: "revenue_intelligence.management_review",
            };

  const forecastBlock: ManagementReviewInsightBlock = {
    insightId: "management_review.forecast_movement",
    label: "Forecast movement",
    title: "Do not use the forecast delta yet",
    body: "Forecast trend and waterfall views currently point to different movement values.",
    evidence: "Executive copy avoids a numeric delta until forecast trend and waterfall sources are reconciled.",
    suggestedAction: "Use What drives the forecast for this review. Reconcile the movement source before quoting a delta.",
    tone: "neutral",
    sourceHelper: "evaluateRiskForecast",
    sourceFields: ["forecastChartData", "forecastWaterfallChartData"],
    requiredAccess: "revenue_intelligence.management_review",
    caveat: "Forecast movement has not been reconciled to one numeric source of truth yet.",
  };

  const urgencyBlock: ManagementReviewInsightBlock =
    input.urgencyRiskCount > 0
      ? {
        insightId: "management_review.near_term_exposure",
        label: "Near-term exposure",
        title: "Near-term risk needs escalation",
        body: "Open pipeline due within 60 days contains urgency exposure.",
        evidence: `${input.urgencyRiskCount} urgency-risk opportunities from the existing risk and value ranking.`,
        suggestedAction: "Escalate the top urgency risks before the next forecast update.",
        tone: "risk",
        sourceHelper: "getManagementReviewUrgencyRisks",
        sourceFields: ["risk_score", "value", "adjustedCloseDate", "is_closed"],
        requiredAccess: "revenue_intelligence.management_review",
      }
      : {
        insightId: "management_review.near_term_exposure",
        label: "Near-term exposure",
        title: "No near-term urgency cluster",
        body: "The 60-day window does not show a concentrated urgency risk.",
        evidence: "No urgency-risk opportunities returned by the existing management review helper.",
        suggestedAction: "Keep attention on coverage and forecast movement.",
        tone: "positive",
        sourceHelper: "getManagementReviewUrgencyRisks",
        sourceFields: ["risk_score", "value", "adjustedCloseDate", "is_closed"],
        requiredAccess: "revenue_intelligence.management_review",
      };

  return [
    coverageBlock,
    forecastBlock,
    urgencyBlock,
  ];
}
